'use client'
import _ from 'lodash'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { FormProvider } from 'react-hook-form'
import { FileIcon, DownloadIcon, CheckCircledIcon } from '@radix-ui/react-icons'
import TextAreaInputController from '@/components/ui/inputs/TextAreaInputController'
import { UploadButton } from '@/shared/ui/buttons/UploadButton'
import { Button } from '@/components/ui/button'
import { Alert } from '@/components/ui/alert'

import { useGetOrderQuery } from '@/core/graphql/hooks'
import { useUploadFile } from '@/shared/model/useUploadFile'
import { useToast } from '@/hooks/use-toast'

import { useBooking } from '../../model/bookingContext'
import { useExpertiseInfoForm, useExpertiseInfoFormSubmit } from '../../model/expertiseInfoForm'

type ExpertiseInfoFormProps = {
  orderId: string
}

export function ExpertiseInfoForm(props: ExpertiseInfoFormProps) {
  const { orderId } = props
  const { toast } = useToast()

  const { state } = useBooking()

  const { data: orderData } = useGetOrderQuery({ variables: { orderId } })
  const [uploadFile, isUploadingFile] = useUploadFile()

  const order = orderData?.meetingLinkOrder

  const rhForm = useExpertiseInfoForm({ comment: state.expertiseData.comment ?? '' })
  const {
    formState: { isValid },
  } = rhForm

  const [onSubmit, isSubmitting] = useExpertiseInfoFormSubmit()

  const onFileUpload = async (file: File) => {
    try {
      await uploadFile(file, orderId, 'order')
    } catch (error: unknown) {
      console.error('Error uploading file:', error)
      toast({
        variant: 'destructive',
        title: 'Error uploading file',
        description: error instanceof Error ? error.message : 'Something went wrong during file upload.',
      })
    }
  }

  return (
    <FormProvider {...rhForm}>
      <form className="flex flex-col space-y-4" onSubmit={rhForm.handleSubmit(onSubmit)}>
        <div className="bg-white shadow-sm sm:shadow-none rounded-lg">
          <div className="px-4 py-6 sm:p-0">
            <p className="text-sm text-gray-500 mb-6">
              <Trans>Please provide additional medical information for your second opinion request</Trans>
            </p>

            {!_.isEmpty(state.formData) && (
              <Alert variant="success" icon={<CheckCircledIcon className="h-4 w-4" />} className="mb-6">
                <h3 className="text-sm font-medium">
                  <Trans>Form submission completed</Trans>
                </h3>
                <div className="mt-2 text-sm">
                  <p>
                    <Trans>Please review the information below before submitting</Trans>
                  </p>
                </div>
              </Alert>
            )}

            <div className="grid grid-rows-auto gap-4">
              <div className="h-fit">
                <TextAreaInputController control={rhForm.control} fullWidth label={t`Comment`} name="comment" rows={3} required />
              </div>
              <div className="h-fit">
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  <Trans>Files</Trans>
                </h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 mb-4">
                  {order?.files?.map((file, index) => (
                    <div key={index} className="relative aspect-h-7 aspect-w-10">
                      <div className="flex flex-col gap-1 items-center justify-center border border-gray-300 rounded-lg">
                        <div className="flex flex-col items-center justify-center">
                          <FileIcon className="w-4 h-4 text-blue-600" />
                          <span className="text-sm break-all text-center px-1">{file.filename}</span>
                        </div>
                        {file.url && (
                          <Button variant="ghost" size="icon">
                            <a href={file.url} target="_blank" rel="noopener noreferrer">
                              <DownloadIcon className="w-4 h-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="relative">
                    <UploadButton onFileUpload={onFileUpload} accept="image/*,.pdf" disabled={isUploadingFile} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end sm:absolute sm:top-full sm:right-0 sm:!m-0 z-10">
          <button
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-gray-300 sm:rounded-none"
            disabled={isSubmitting || !isValid}
            type="submit"
          >
            {isSubmitting && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />}
            <Trans>Submit</Trans>
          </button>
        </div>
      </form>
    </FormProvider>
  )
}
